// 1. React imports
import { useEffect, useRef } from 'react';

// 2. External libraries
import { Link } from '@tanstack/react-router';
import { CircleCheck, Link2, MessageSquare } from 'lucide-react';

// 3. Internal components
import { BrandCard } from '~/components/common/BrandCard';
import { Button } from '~/components/ui/button';
import { CardContent } from '~/components/ui/card';

/**
 * Props for CompletionStep component.
 *
 * Per §TS.3.2: Descriptive props interface naming.
 */
export interface CompletionStepProps {
  topicId: string;
  onComplete: () => void;
}

/**
 * Completion step for onboarding wizard.
 *
 * Displays congratulations message with suggested next steps:
 * - Add more Connections card - navigates to topic detail page
 * - Add more Topics card - navigates to topics list page
 * - Go to Dashboard button - calls onComplete callback
 *
 * Per §TS.3.1: Function declaration for named export.
 *
 * @example
 * ```tsx
 * <CompletionStep
 *   topicId="topic_123"
 *   onComplete={() => navigate({ to: '/' })}
 * />
 * ```
 */
export function CompletionStep({ topicId, onComplete }: CompletionStepProps) {
  const headingRef = useRef<HTMLHeadingElement>(null);

  // Move focus to the step heading on mount, matching the focus behavior of
  // the earlier wizard steps (WizardProgress) when arriving at the final step.
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return (
    <div className="text-center">
      {/* Success Icon */}
      <CircleCheck className="mx-auto mb-4 size-20 text-success" />

      {/* Success Heading */}
      <h1
        ref={headingRef}
        tabIndex={-1}
        className="mb-2 font-heading text-2xl font-semibold tracking-tight outline-none"
      >
        Congratulations!
      </h1>

      {/* Success Message */}
      <p className="mb-8 text-base text-muted-foreground">
        You've successfully sent and retrieved your first message with DeadDrop.
      </p>

      {/* Suggested Next Steps */}
      <h3 className="mb-4 text-left font-heading text-lg font-semibold">What's Next?</h3>

      <div className="mb-8 flex flex-col gap-4">
        {/* Add more Connections Card */}
        <Link to="/topics/$topicId" params={{ topicId }} className="block">
          <BrandCard accent="left" glow="subtle" interactive>
            <CardContent>
              <div className="flex items-start gap-4">
                <Link2 className="mt-1 size-8 text-primary" />
                <div className="text-left">
                  <p className="text-base font-semibold">Add more Connections</p>
                  <p className="text-sm text-muted-foreground">
                    Connect more services like Discord, Slack, or webhooks to this topic
                  </p>
                </div>
              </div>
            </CardContent>
          </BrandCard>
        </Link>

        {/* Add more Topics Card */}
        <Link to="/" className="block">
          <BrandCard accent="left" glow="subtle" interactive>
            <CardContent>
              <div className="flex items-start gap-4">
                <MessageSquare className="mt-1 size-8 text-primary" />
                <div className="text-left">
                  <p className="text-base font-semibold">Add more Topics</p>
                  <p className="text-sm text-muted-foreground">
                    Create additional topics to organize your message streams
                  </p>
                </div>
              </div>
            </CardContent>
          </BrandCard>
        </Link>
      </div>

      {/* Go to Dashboard Button */}
      <Button variant="default" size="lg" className="w-full" onClick={onComplete}>
        Go to Dashboard
      </Button>
    </div>
  );
}
